import { colors } from '@ctw/config/zui';
import { globalStyles } from '@ctw/shared/content/document-viewer/_internal/renderers/pdf/pdf-global-styles';
import { PdfHeadingRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/pdf/pdf-heading-renderer';
import { PdfHtmlRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/pdf/pdf-html-renderer';
import { PdfLinkRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/pdf/pdf-link-renderer';
import { PdfListRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/pdf/pdf-list-renderer';
import { PdfTableRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/pdf/pdf-table-renderer';
import {
  elementNamesIntentionallyOmittedFromRendering,
  filterChildNodesByNodeName,
  isParsedBrElement,
  isParsedCdataSection,
  isParsedDivElement,
  isParsedDivTextElement,
  isParsedHeadingElement,
  isParsedHtmlElement,
  isParsedLinkElement,
  isParsedListElement,
  isParsedParagraphElement,
  isParsedSpanElement,
  isParsedTableElement,
  isParsedTextNode,
  isSafeParsedHtmlElement,
  nodeChildTreeContainsNodeWithNames,
  textContentFromNodeTree,
  uniqueIdentifierForNode,
  willNodeBeRenderedAsTextElement,
} from '@ctw/shared/utils/cda/helpers';
import type { CdaParsedHtmlNode } from '@ctw/shared/utils/cda/types';
import { Text, View } from '@react-pdf/renderer';
import { Fragment, memo } from 'react';

interface NodeRendererProps {
  id: string;
  node: CdaParsedHtmlNode;
  indentLevel?: number;
}

export const PdfNodeRenderer = memo(({ id, node, indentLevel = 0 }: NodeRendererProps) => {
  const isElementWithChildNodes = node.nodeType === 'element' || node.nodeType === 'cdata_section';
  const hasTextNodes =
    isElementWithChildNodes &&
    node.childNodes.some((child) => willNodeBeRenderedAsTextElement(child));
  const hasNonTextNodes =
    isElementWithChildNodes &&
    node.childNodes.some((child) => !willNodeBeRenderedAsTextElement(child));

  if (hasTextNodes && hasNonTextNodes) {
    const childNodes = node.childNodes;
    const newChildNodes: Array<CdaParsedHtmlNode> = [];
    let currentTextGroup: Array<CdaParsedHtmlNode> = [];
    const addCurrentTextGroup = () => {
      if (currentTextGroup.length > 0) {
        newChildNodes.push({
          nodeType: 'element',
          nodeName: 'content',
          childNodes: currentTextGroup,
          isEffectivelyEmpty: currentTextGroup.every((child) => child.isEffectivelyEmpty),
          attributes: {},
          styles: {
            pdfStyles: {},
            className: '',
            extensions: [],
          },
        });
        currentTextGroup = [];
      }
    };
    for (const childNode of childNodes) {
      if (willNodeBeRenderedAsTextElement(childNode)) {
        currentTextGroup.push(childNode);
      } else {
        addCurrentTextGroup();
        newChildNodes.push(childNode);
      }
    }
    addCurrentTextGroup();

    node.childNodes = newChildNodes;
  } else if (hasTextNodes && !willNodeBeRenderedAsTextElement(node) && node.childNodes.length > 1) {
    node.childNodes = [
      {
        nodeType: 'element',
        nodeName: 'content',
        childNodes: node.childNodes,
        isEffectivelyEmpty: node.childNodes.every((child) => child.isEffectivelyEmpty),
        attributes: {},
        styles: {
          pdfStyles: {},
          className: '',
          extensions: [],
        },
      },
    ];
  }

  if (isParsedBrElement(node)) {
    return <Text>{'\n'}</Text>;
  }

  if (elementNamesIntentionallyOmittedFromRendering.includes(node.nodeName as never)) {
    switch (node.nodeName) {
      case 'renderMultiMedia':
        return (
          <View style={globalStyles.alertBox}>
            <Text style={globalStyles.alertText}>
              This portion of the document contains multimedia XML which cannot be displayed. Please
              download the full XML file to review.
            </Text>
          </View>
        );
      default:
        return (
          <View style={globalStyles.alertBox}>
            <Text style={globalStyles.alertText}>
              This portion of the document contains content which cannot be displayed. Please
              download the full XML file to review.
            </Text>
          </View>
        );
    }
  }

  if (node.isEffectivelyEmpty) {
    // Ignore nodes that are effectively empty. Note that in some cases these are omitted entirely
    // by the CDA parser for payload optimization, but this explicit guard prevents us from rendering
    // them if they end up here.
    return <></>;
  }

  if (isParsedTextNode(node)) {
    const nodeIdentifier = uniqueIdentifierForNode({
      node,
      context: id,
      purpose: 'node-generictext',
    });

    const cleanedText = node.textContent?.replaceAll(/\s+/gm, ' ').trim();
    return (
      <Text style={globalStyles.text} key={nodeIdentifier}>
        {cleanedText}
      </Text>
    );
  }

  if (isParsedCdataSection(node)) {
    const nodeIdentifier = uniqueIdentifierForNode({
      node,
      context: id,
      purpose: 'node-generictext',
    });

    return (
      <View style={globalStyles.text} key={nodeIdentifier}>
        <PdfHtmlRenderer parsedHtmlNodes={node.childNodes} id={nodeIdentifier} />
      </View>
    );
  }

  if (isParsedHtmlElement(node)) {
    if (isParsedLinkElement(node)) {
      const nodeIdentifier = uniqueIdentifierForNode({
        node,
        context: id,
        purpose: 'html',
      });
      return <PdfLinkRenderer id={nodeIdentifier} link={node} />;
    }

    /*
     * footnote: https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#footnote-and-footnoteref
     * caption: https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#caption
     * content: https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#content
     */
    if (['content', 'caption', 'footnote'].includes(node.nodeName)) {
      const nodeIdentifier = uniqueIdentifierForNode({
        node,
        context: id,
        purpose: node.nodeName,
      });

      return (
        <Text
          id={nodeIdentifier}
          key={nodeIdentifier}
          style={[
            node.styles.pdfStyles,
            node.nodeName === 'content'
              ? {}
              : {
                  width: '100%',
                  minWidth: '100%',
                },
            node.nodeName === 'caption'
              ? {
                  width: '100%',
                  minWidth: '100%',
                  fontSize: 8,
                  fontStyle: 'italic',
                  color: colors.content.subtle,
                  paddingTop: 1,
                }
              : {},
            node.nodeName === 'footnote'
              ? {
                  fontSize: 8,
                  color: colors.content.subtle,
                  paddingLeft: 2,
                  paddingRight: 2,
                  paddingTop: 1,
                  paddingBottom: 1,
                }
              : {},
          ]}
        >
          {node.childNodes.map((child, index) => {
            const nodeChildIdentifier = uniqueIdentifierForNode({
              node: child,
              context: nodeIdentifier,
              purpose: 'node-generictext',
              index,
            });

            return (
              <PdfNodeRenderer id={nodeChildIdentifier} key={nodeChildIdentifier} node={child} />
            );
          })}
        </Text>
      );
    }

    if (isParsedListElement(node)) {
      const listIdentifier = uniqueIdentifierForNode({
        node,
        context: id,
        purpose: 'semantic-list',
      });

      if (nodeChildTreeContainsNodeWithNames(node, ['table', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'])) {
        // The presence of certain elements inside a list element indicate that this is not a HTML-semantic list,
        // but rather a set of items that should be treated as ordered elements. Skip the list item
        // itself and render its children as independent nodes.
        const items = filterChildNodesByNodeName(node, ['item', 'li'], false);

        return (
          <Fragment key={listIdentifier}>
            {items.map((item, index) => {
              if (item.childNodes.length > 1) {
                item.childNodes = [
                  {
                    nodeType: 'element',
                    nodeName: 'div',
                    childNodes: item.childNodes,
                    styles: {
                      pdfStyles: {},
                      className: '',
                      extensions: [],
                    },
                    isEffectivelyEmpty: item.childNodes.every((child) => child.isEffectivelyEmpty),
                    attributes: {},
                  },
                ];
              }

              const listItemIdentifier = uniqueIdentifierForNode({
                node,
                context: listIdentifier,
                purpose: 'semantic-list-item',
                index,
              });

              if (item.childNodes[0]) {
                return (
                  <View
                    id={listItemIdentifier}
                    key={listItemIdentifier}
                    style={isParsedHtmlElement(item) ? item.styles.pdfStyles : undefined}
                  >
                    <PdfNodeRenderer
                      id={listItemIdentifier}
                      key={listItemIdentifier}
                      node={item.childNodes[0]}
                    />
                  </View>
                );
              }

              return undefined;
            })}
          </Fragment>
        );
      }

      return (
        <PdfListRenderer
          id={listIdentifier}
          key={listIdentifier}
          list={node}
          nestingLevel={indentLevel}
        />
      );
    }

    if (isParsedHeadingElement(node)) {
      const headingIdentifier = uniqueIdentifierForNode({
        node,
        context: id,
        purpose: 'heading',
      });
      return <PdfHeadingRenderer id={headingIdentifier} heading={node} />;
    }

    // https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#table
    if (isParsedTableElement(node)) {
      const tableIdentifier = uniqueIdentifierForNode({
        node,
        context: id,
        purpose: 'table',
      });

      return <PdfTableRenderer id={tableIdentifier} table={node} />;
    }

    // https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#sub-and-sup
    if (isParsedParagraphElement(node)) {
      const paragraphIdentifier = uniqueIdentifierForNode({
        node,
        context: id,
        purpose: 'paragraph',
      });

      return (
        <Text
          id={paragraphIdentifier}
          key={paragraphIdentifier}
          style={[node.styles.pdfStyles, { textIndent: 5, width: '100%' }]}
        >
          {node.childNodes.map((child, index) => {
            const childNodeIdentifier = uniqueIdentifierForNode({
              node: child,
              context: paragraphIdentifier,
              purpose: 'paragraph-child',
              index,
            });

            return (
              <PdfNodeRenderer id={childNodeIdentifier} key={childNodeIdentifier} node={child} />
            );
          })}
        </Text>
      );
    }

    if (isParsedDivElement(node)) {
      const divIdentifier = uniqueIdentifierForNode({
        node,
        context: id,
        purpose: 'div',
      });
      return (
        <View id={divIdentifier} key={divIdentifier} style={node.styles.pdfStyles}>
          {node.childNodes.map((child, index) => {
            const childNodeIdentifier = uniqueIdentifierForNode({
              node: child,
              context: divIdentifier,
              purpose: 'div-child',
              index,
            });

            return (
              <PdfNodeRenderer id={childNodeIdentifier} key={childNodeIdentifier} node={child} />
            );
          })}
        </View>
      );
    }

    if (isParsedDivTextElement(node)) {
      const divTextIdentifier = uniqueIdentifierForNode({
        node,
        context: id,
        purpose: 'div-text',
      });
      return (
        <Text id={divTextIdentifier} key={divTextIdentifier} style={node.styles.pdfStyles}>
          {node.childNodes.map((child, index) => {
            const childNodeIdentifier = uniqueIdentifierForNode({
              node: child,
              context: divTextIdentifier,
              purpose: 'div-text-child',
              index,
            });

            return (
              <PdfNodeRenderer id={childNodeIdentifier} key={childNodeIdentifier} node={child} />
            );
          })}
        </Text>
      );
    }

    if (isParsedSpanElement(node)) {
      const spanIdentifier = uniqueIdentifierForNode({
        node,
        context: id,
        purpose: 'span',
      });

      return (
        <Text id={spanIdentifier} style={node.styles.pdfStyles}>
          {textContentFromNodeTree(node)}
        </Text>
      );
    }

    /*
     * CDA narratives can technically contain any HTML/XML tag. Any tag we do not explicitly handle
     * above, we render as-is below if it exists in our safelist.
     *
     * This includes HTML tags we don't explicitly handle, as well as the following from the CDA spec:
     *  - `sup`/`sub`: https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#sub-and-sup
     */
    if (isSafeParsedHtmlElement(node)) {
      return (
        <>
          {/* Cast because we know this is an HTMLElement from isSafeHtmlElement */}
          {node.childNodes.map((child, index) => {
            const nodeWrapperIdentifier = uniqueIdentifierForNode({
              node: child,
              context: id,
              purpose: 'node-wrapper',
              index,
            });

            return (
              <PdfNodeRenderer
                id={nodeWrapperIdentifier}
                key={nodeWrapperIdentifier}
                node={child}
                indentLevel={indentLevel}
              />
            );
          })}
        </>
      );
    }
  }

  // Any unknown node should not be rendered.
  return <></>;
});
